import { Button, Checkbox, Input, Message, useToaster } from 'rsuite';
import React from 'react';

import * as galleryApi from '../../api/gallery';
import { FormField, FormModal } from '../../common/FormModal';
import LanguageTabs, {
  getLanguagePanelId,
  getLanguageTabId,
  LanguageKey,
} from '../../common/LanguageTabs';
import { useFormErrors } from '../../common/useFormErrors';

type Props = {
  open: boolean;
  setOpen: (newValue: boolean) => void;
  callback: () => void;
};

export default function CreateSectionModal({ open, setOpen, callback }: Props) {
  const toaster = useToaster();
  const { errorSummary, setApiError, clearErrors } = useFormErrors({
    fallbackMessage: 'Не вдалося створити секцію. Спробуйте пізніше',
    resetOn: [open],
  });
  const [modalSectionName, setModalSectionName] = React.useState<string>('');
  const [modalSectionNameEn, setModalSectionNameEn] =
    React.useState<string>('');
  const [modalSectionNameDe, setModalSectionNameDe] =
    React.useState<string>('');
  const [modalSectionActive, setModalSectionActive] =
    React.useState<boolean>(false);
  const [activeLanguage, setActiveLanguage] = React.useState<LanguageKey>('ua');
  const languageTabPrefix = 'gallery-create-section-language';

  React.useEffect(() => {
    if (open) {
      setActiveLanguage('ua');
    }
  }, [open]);

  const languageConfig: Record<
    LanguageKey,
    { value: string; setValue: (value: string) => void }
  > = {
    ua: { value: modalSectionName, setValue: setModalSectionName },
    en: { value: modalSectionNameEn, setValue: setModalSectionNameEn },
    de: { value: modalSectionNameDe, setValue: setModalSectionNameDe },
  };

  const activeLanguageConfig = languageConfig[activeLanguage];

  return (
    <FormModal
      open={open}
      onClose={() => setOpen(false)}
      title="Створити курс"
      errorSummary={errorSummary}
      footer={
        <>
          <Button
            onClick={async () => {
              clearErrors();
              try {
                await galleryApi.createSection(
                  modalSectionName,
                  modalSectionNameEn,
                  modalSectionNameDe,
                  modalSectionActive
                );
                toaster.push(
                  <Message type="success" showIcon closable>
                    Секцію створено
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
                setOpen(false);
                setModalSectionName('');
                setModalSectionNameEn('');
                setModalSectionNameDe('');
                setModalSectionActive(false);
                callback();
              } catch (err: any) {
                console.error(err);
                const errorMessage = setApiError(err)[0];
                toaster.push(
                  <Message type="error" showIcon closable>
                    {errorMessage}
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
              }
            }}
            appearance="primary"
          >
            Створити
          </Button>
          <Button onClick={() => setOpen(false)} appearance="subtle">
            Скасувати
          </Button>
        </>
      }
    >
      <div className="form-modal__toggles">
        <Checkbox
          checked={modalSectionActive}
          onChange={(_value, checked) => {
            setModalSectionActive(checked);
          }}
        >
          Статус відображення на сайті
        </Checkbox>
      </div>
      <LanguageTabs
        activeLanguage={activeLanguage}
        onChange={setActiveLanguage}
        idPrefix={languageTabPrefix}
      />
      <div
        className="language-panel"
        role="tabpanel"
        id={getLanguagePanelId(languageTabPrefix, activeLanguage)}
        aria-labelledby={getLanguageTabId(languageTabPrefix, activeLanguage)}
      >
        <div className="language-fields">
          <FormField label="Назва">
            <Input
              placeholder="Назва"
              value={activeLanguageConfig.value || ''}
              onChange={activeLanguageConfig.setValue}
            />
          </FormField>
        </div>
      </div>
    </FormModal>
  );
}
